import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

const AboutSection = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const y1 = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const y2 = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto"
      data-testid="about-section"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
        {/* Text Content - Left */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <p
            className="text-xs sm:text-sm uppercase tracking-[0.25em] font-semibold text-[#2C6E63] mb-6"
            data-testid="about-overline"
          >
            About Royal Middle East
          </p>
          <h2
            className="font-heading text-4xl sm:text-5xl font-light tracking-tight leading-[1.15] text-[#2D3436] mb-8"
            data-testid="about-heading"
          >
            Crafting Excellence in Every Detail
          </h2>
          <div className="space-y-6 text-base font-normal leading-relaxed text-[#6B6560]">
            <motion.p 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              data-testid="about-description-1"
            >
              At Royal Middle East International LLC (ROY.INOX), we are specialists in designing, fabricating and installing high-performance F&B and commercial kitchen equipment. As a privately owned facility with in-house manufacturing and maintenance teams, we take pride in transforming concepts into reality on time, every time.
            </motion.p>
            <motion.p 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              data-testid="about-description-2"
            >
              Founded in 1997 and backed by over 80 years of combined industry experience, we understand the demands of modern food service environments. From custom stainless-steel fabrication to precision refrigeration systems, our solutions are built for durability, efficiency and lasting value.
            </motion.p>
            <motion.p 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              data-testid="about-description-3"
            >
              Our state-of-the-art, 35,000 sq ft manufacturing facility at Dubai Investment Park enables us to deliver tailored solutions across the UAE.
            </motion.p>
          </div>

          {/* Decorative Line */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            viewport={{ once: true }}
            className="mt-12 flex items-center gap-4"
          >
            <motion.div 
              initial={{ width: 0 }}
              whileInView={{ width: 64 }}
              transition={{ duration: 1, delay: 1 }}
              viewport={{ once: true }}
              className="h-px bg-[#2C6E63]"
            ></motion.div>
            <span className="text-sm font-medium text-[#2C6E63] tracking-wide">25+ Years of Excellence</span>
          </motion.div>
        </motion.div>

        {/* Images - Right (Overlapping) */}
        <div className="relative h-[500px] lg:h-[600px]">
          <motion.div
            style={{ y: y1 }}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="absolute top-0 right-0 w-[65%] h-[60%] overflow-hidden shadow-2xl"
          >
            <img
              src="/projects/2024 Wine display .jpeg"
              alt="Wine display installation"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
              data-testid="about-image-1"
            />
          </motion.div>
          <motion.div
            style={{ y: y2 }}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            viewport={{ once: true }}
            className="absolute bottom-0 left-0 w-[65%] h-[60%] overflow-hidden shadow-2xl border-8 border-[#FAF8F5]"
          >
            <img
              src="/projects/2022 FOH 03.JPG"
              alt="Front of house installation"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
              data-testid="about-image-2"
            />
          </motion.div>
          {/* Decorative Element */}
          <motion.div 
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
            className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-[#D4C4A8] -z-10"
          ></motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
